import { FC, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDropzone } from 'react-dropzone';
import { RxUtils, Tr } from '@utils';
import { loadingSpinnerOverlayService, stockService } from '@services';
import { Button, Link, LinkProps, Stack } from '@mui/material';
import { PortalPageTitle, BulkUploadStepCard } from '@components/portal/common';

const paddingX = 9;

export const BulkUpdate: FC = () => {
	const navigate = useNavigate();

	const download: LinkProps['onClick'] = (e) => {
		e.preventDefault();
		RxUtils.promisify(stockService.downloadBulkTemplateFile(), (data) => {
			const url = window.URL.createObjectURL(new Blob([data]));
			const link = document.createElement('a');
			link.href = url;
			link.setAttribute('download', 'template.xlsx');
			document.body.appendChild(link);
			link.click();
			link.remove();
		}).catch(RxUtils.noop);
	};

	const { open, acceptedFiles, getInputProps } = useDropzone({
		noClick: true,
		noKeyboard: true,
		noDrag: true,
		multiple: false,
		accept: {
			'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': ['.xls', '.xlsx'],
			'application/vnd.ms-excel': ['.xls', '.xlsx']
		}
	});

	useEffect(() => {
		const [file] = acceptedFiles;
		if (file) {
			loadingSpinnerOverlayService.increment();
			const formData = new FormData();
			formData.append('file', file, file.name);
			RxUtils.promisify(
				stockService.uploadBulkTemplateFile(formData),
				() => loadingSpinnerOverlayService.reset(() => navigate('..')),
				() => loadingSpinnerOverlayService.reset()
			);
		}
	}, [acceptedFiles]);

	return (
		<Stack direction="column" spacing={6} sx={{ height: 1.0, maxHeight: 1.0, paddingY: 5 }}>
			<PortalPageTitle
				paddingX={3}
				title={<Tr.Portal path="stock.bulk-update.title" />}
				subtitle={<Tr.Portal path="stock.bulk-update.subtitle" />}
				onBackButtonClick={() => navigate(-1)}
			/>
			<Stack direction="column" sx={{ paddingX, flexGrow: 1 }} justifyContent="space-around" alignItems="center">
				<BulkUploadStepCard
					step="download"
					title={<Tr.Portal path="stock.bulk-update.download-step-title" />}
					subtitle={
						<Link href="#" target="_blank" underline="hover" onClick={download} data-testid="download-sample-file-link">
							<Tr.Portal path="stock.bulk-update.download-step-subtitle" />
						</Link>
					}
				/>
				<BulkUploadStepCard step="fill" title={<Tr.Portal path="stock.bulk-update.fill-step-title" />} />
				<BulkUploadStepCard
					step="upload"
					title={<Tr.Portal path="stock.bulk-update.upload-step-title" />}
					subtitle={<Tr.Portal path="stock.bulk-update.upload-step-subtitle" />}
				/>
			</Stack>
			<Stack direction="column" alignItems="center" justifyContent="center">
				<Button data-testid="stock-bulk-update-upload-file-button" variant="contained" onClick={open}>
					<Tr.Portal path="stock.bulk-update.upload-spreadsheet-button" />
				</Button>
				<input {...getInputProps()} style={{ display: 'none ' }} />
			</Stack>
		</Stack>
	);
};
